// A game server inside the page. The Authority is transport-agnostic, so the browser can be
// its third host: the Create tab starts one over the game it has just built, the game view
// connects to it through a loopback instead of a WebSocket, and a few bots join through the
// same loopback. Everything else (hello, snapshots, interpolation, the HUD) is the real
// netcode path, with no network. Only a draft plays this way; a published game runs on the
// server like any other.

import { Authority, type Socket } from "../server/authority.js";
import type { GameModule } from "../contract.js";
import { makeRng } from "../shared/rng.js";
import type { ServerMsg } from "../shared/protocol.js";

/** The little a client needs from a connection; game.ts wraps a WebSocket in the same shape. */
export interface Transport {
  send(text: string): void;
  close(): void;
  onopen: (() => void) | null;
  onmessage: ((text: string) => void) | null;
  onclose: ((code: number, reason: string) => void) | null;
}

export interface LocalServer {
  readonly authority: Authority;
  /** Open a loopback connection into a room of the one game this server holds. */
  connect(room?: string): Transport;
  /** Random-walk bots, as `npm run bots` would send them, so a draft is not played alone. */
  addBots(n: number, seed?: number): void;
  close(): void;
}

const BOT_NAMES = ["ada", "ben", "cy", "dee", "eli", "fay", "gus", "hal", "ivy", "jo"];

export function startLocalServer(module: GameModule, slug: string, blurb: string): LocalServer {
  const authority = new Authority([{ slug, blurb, module }]);
  const bots: { transport: Transport; timer: ReturnType<typeof setInterval> }[] = [];
  let closed = false;

  // Deliveries hop through the microtask queue so neither side re-enters the other: the
  // authority answers hello synchronously inside message(), which must not land back in the
  // client's handler while its own send() is still on the stack.
  const later = (fn: () => void) => queueMicrotask(fn);

  const connect = (room = "main"): Transport => {
    let open = true;
    const transport: Transport = {
      onopen: null,
      onmessage: null,
      onclose: null,
      send(text) {
        if (open) conn.message(text);
      },
      close() {
        if (!open) return;
        open = false;
        conn.gone();
        later(() => transport.onclose?.(1000, "closed"));
      },
    };
    const socket: Socket = {
      send(text) {
        if (open) later(() => transport.onmessage?.(text));
      },
      close(code, reason) {
        if (!open) return;
        open = false;
        conn.gone();
        later(() => transport.onclose?.(code, reason));
      },
      get open() {
        return open;
      },
    };
    const conn = authority.connect(socket, slug, room);
    later(() => transport.onopen?.());
    return transport;
  };

  return {
    authority,
    connect,
    addBots(n, seed = 1) {
      const { w, h } = module.meta.world;
      for (let i = 0; i < n; i++) {
        const transport = connect("main");
        const rng = makeRng(seed * 1000 + i);
        let you: number | undefined;
        let tx = w / 2;
        let ty = h / 2;
        let retargetAt = 0;
        transport.onopen = () => transport.send(JSON.stringify({ t: "hello", name: BOT_NAMES[i % BOT_NAMES.length] }));
        transport.onmessage = (text) => {
          const msg = JSON.parse(text) as ServerMsg;
          if (msg.t === "s") you = msg.you;
        };
        const timer = setInterval(() => {
          if (you === undefined) return;
          const now = performance.now();
          if (now >= retargetAt) {
            tx = rng.range(0, w);
            ty = rng.range(0, h);
            retargetAt = now + rng.range(1000, 3000);
          }
          transport.send(JSON.stringify({ t: "in", i: { tx, ty, a: rng.next() < 0.1, b: rng.next() < 0.05 } }));
        }, 100);
        bots.push({ transport, timer });
      }
    },
    close() {
      if (closed) return;
      closed = true;
      for (const b of bots) {
        clearInterval(b.timer);
        b.transport.close();
      }
      bots.length = 0;
      authority.close();
    },
  };
}
